import type { JSX } from "react";
import { useEffect, useState } from "react";

export interface GuideNavSection {
  id: string;
  heading: string;
  index: number;
}

/**
 * The guide's contents strip: one anchor per rendered section, numbered exactly
 * as that section's own eyebrow is numbered. Sticky from the tablet breakpoint
 * up and a plain in-flow wrapping row on a phone — the stylesheet decides, so the
 * phone gets the same list without a second copy of it in the markup.
 *
 * One IntersectionObserver marks the section the reader is in, never a scroll
 * listener: ProgressBar, GuideHeaderMotion's tilt and GuideParallax each already
 * own one on this page, and a fourth callback per frame to move a marker that
 * only changes when a whole section arrives would be the wrong instrument. The
 * band is narrowed to the middle of the viewport (-45% on both edges) so exactly
 * one section is current. With no JavaScript the anchors still work — only the
 * current marker is absent, so nothing is hidden and nothing is unreachable.
 *
 * It keeps its own state and renders no section of its own for the reason
 * CityClock documents: the current-section change must not re-render the page
 * above a fixed background canvas.
 */
export function GuideNav({ sections }: { sections: GuideNavSection[] }): JSX.Element | null {
  const [current, setCurrent] = useState<string | null>(null);
  // The effect keys on the ids, not the array: CityPageBody rebuilds the specs
  // on every render, so an array dependency would re-observe on every pass.
  const ids = sections.map((section) => section.id).join("|");

  useEffect(() => {
    if (typeof IntersectionObserver !== "function") return undefined;
    const targets = ids
      .split("|")
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (targets.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setCurrent(entry.target.id);
        }
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
    );
    for (const target of targets) observer.observe(target);
    return () => observer.disconnect();
  }, [ids]);

  if (sections.length === 0) return null;

  return (
    <nav className="atlas-guide-col atlas-guide-nav" aria-label="Guide sections">
      <ul className="atlas-guide-nav-list">
        {sections.map((section) => (
          <li key={section.id}>
            <a
              className="atlas-guide-nav-link"
              href={`#${section.id}`}
              aria-current={current === section.id ? "location" : undefined}
            >
              <span className="atlas-guide-nav-num">{String(section.index).padStart(2, "0")}</span>
              <span className="atlas-guide-nav-label">{section.heading}</span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
